import { useCallback } from "react";

import { useUserTriggeredModalsHistoryState } from "../history/use-modal-history-states";
import { useModalHistoryStepCounter } from "../history/use-modal-history-step-counter";
import type { ModalComponent } from "../modal-builder";
import { type ModalWithOptionalDeduplicationKey, resolveModalId } from "../modal-id";
import { useUserTriggeredModalsStore } from "../user-triggered-modals.store";

export const useModalReplaceOperations = () => {
  const [, , pushTemporaryModalsState, peekTemporaryModalsState] = useUserTriggeredModalsHistoryState();

  const peekOpenIds = useUserTriggeredModalsStore((state) => state.peekOpenIds);
  const setArgs = useUserTriggeredModalsStore((state) => state.setArgs);

  const countStepsToReachModalState = useModalHistoryStepCounter();

  const replaceUserTriggeredModalById = useCallback(
    (originalId: string, id: string, args?: unknown) => {
      const temporaryModalsState = peekTemporaryModalsState();

      if (!temporaryModalsState.find((modal) => modal.id === originalId)) {
        return;
      }

      setArgs(id, args);
      pushTemporaryModalsState(temporaryModalsState.map((modal) => (modal.id === originalId ? { id, args, state: null } : modal)));
    },
    [peekTemporaryModalsState, pushTemporaryModalsState, setArgs],
  );

  const replaceUserTriggeredModal = useCallback(
    <Args>(
      originalModal: ModalWithOptionalDeduplicationKey<ModalComponent<never>>,
      newModal: ModalWithOptionalDeduplicationKey<ModalComponent<never>>,
      args?: Args,
    ) => {
      replaceUserTriggeredModalById(resolveModalId(originalModal), resolveModalId(newModal), args);
    },
    [replaceUserTriggeredModalById],
  );

  const replaceBackUserTriggeredModal = useCallback(
    <Args>(
      currentModal: ModalWithOptionalDeduplicationKey<ModalComponent<never>>,
      originalModal: ModalWithOptionalDeduplicationKey<ModalComponent<never>>,
      args?: Args,
    ) => {
      const openModalIds = peekOpenIds();

      const currentId = resolveModalId(currentModal);
      const originalId = resolveModalId(originalModal);

      const expectedModalIds = openModalIds.map((id) => (id === currentId ? originalId : id));
      const stepsToGoBack = countStepsToReachModalState(expectedModalIds);

      if (stepsToGoBack !== null) {
        setArgs(originalId, args);
        window.history.go(-stepsToGoBack);
        return;
      }

      replaceUserTriggeredModalById(currentId, originalId, args);
    },
    [peekOpenIds, countStepsToReachModalState, replaceUserTriggeredModalById, setArgs],
  );

  return { replaceUserTriggeredModal, replaceBackUserTriggeredModal };
};
